/**
 * What a threat level turns into on the ground.
 *
 *   maps/biter_battles_v2/ai.lua — select_units_around_spawner, spawn_biters,
 *                                  get_threat_ratio, subtract_threat
 *   maps/biter_battles_v2/biter_raffle.lua — which unit each roll produces
 *
 * The mechanic that makes this not a division: **spawning never spends
 * threat.** `select_units_around_spawner` budgets from a *local* copy of
 * `storage.bb_threat`; the stock itself is only decremented in
 * `subtract_threat`, when a unit dies. So a wave is a fraction of the stock,
 * the stock is debited later, and the level it holds is where those two
 * balance.
 *
 * Finding that level is a fixed point, not a formula: the budget checks are
 * granular — a boss is affordable or it is not — and averaging them before
 * solving deletes the behaviour. So it is solved by running the spawn loop
 * against a constant income until the two agree.
 */

import { DEFAULTS, BB_CONFIG } from './constants';
import { biterHealthFactor } from './feeding';
import type { Ruleset } from './rulesets';
import {
  UNIT_TIERS,
  bossSpawnAttempts,
  maxGroupSize,
  raffleWeights,
  spawnerThreatFactor,
  type UnitTier,
} from './strength';

/** Sides alternate on a one-minute timer, so each side is attacked every 2 min. */
export const WAVE_CYCLE_SECONDS = 120;

/** `unit_type_raffle` — ai.lua:16. Rolled once per `spawn_biters` call. */
const GROUP_TYPES = ['biter', 'mixed', 'mixed', 'spitter', 'spitter'] as const;

export type TierCounts = Record<UnitTier, number>;

/**
 * Biters and spitters are the same raffle but not the same price. On live the
 * two cost the same and the distinction is flavour; on every branch that
 * renormalises `threat_values` it is real money — a 3v3 big biter is 25 and a
 * big spitter 13.3 — so the tally carries the split rather than throwing it
 * away at the point it is counted.
 */
export type UnitKind = 'biter' | 'spitter';
export const UNIT_KINDS: UnitKind[] = ['biter', 'spitter'];

export type KindCounts = Record<UnitKind, TierCounts>;

const zeroTiers = (): TierCounts => ({ small: 0, medium: 0, big: 0, behemoth: 0 });

const zeroKinds = (): KindCounts => ({ biter: zeroTiers(), spitter: zeroTiers() });

/** True when this branch charges different threat for a biter and a spitter. */
export function pricesKindsApart(ruleset: Ruleset): boolean {
  return UNIT_TIERS.some(
    (tier) => ruleset.threatValues[`${tier}-biter`] !== ruleset.threatValues[`${tier}-spitter`],
  );
}

/** Deterministic PRNG — the same inputs must always draw the same wave. */
function mulberry32(seed: number): () => number {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** `math.random(lo, hi)` — inclusive at both ends. */
const randomInt = (rng: () => number, lo: number, hi: number) =>
  lo + Math.floor(rng() * (hi - lo + 1));

export interface WaveContext {
  ruleset: Ruleset;
  /** Uncapped evolution, 1 = 100%. */
  evo: number;
  maxReanimThresh: number;
}

/**
 * Everything constant for one evolution, resolved once.
 *
 * This exists purely for speed, and the speed is what makes an evolution sweep
 * possible: the naive form allocates a weights object and builds a unit-name
 * string on every roll, and a sweep is millions of rolls.
 */
interface Compiled {
  /** Cumulative raffle weights over the four tiers. */
  cumulative: Float64Array;
  rollCeiling: number;
  /** Threat price, indexed `tier * 2 + (spitter ? 1 : 0)`. */
  price: Float64Array;
  health: number;
  groupSize: number;
  bossesActive: boolean;
  bossAttempts: number;
}

function compile(ctx: WaveContext): Compiled {
  const weights = raffleWeights(ctx.evo);
  const cumulative = new Float64Array(4);
  let running = 0;
  UNIT_TIERS.forEach((tier, index) => {
    running += weights[tier];
    cumulative[index] = running;
  });

  const price = new Float64Array(8);
  UNIT_TIERS.forEach((tier, index) => {
    price[index * 2] = ctx.ruleset.threatValues[`${tier}-biter`];
    price[index * 2 + 1] = ctx.ruleset.threatValues[`${tier}-spitter`];
  });

  const groupSize = maxGroupSize(ctx.evo);
  return {
    cumulative,
    rollCeiling: Math.floor(running),
    price,
    health: biterHealthFactor(ctx.evo, ctx.maxReanimThresh),
    groupSize,
    bossesActive: groupSize !== DEFAULTS.maxGroupSizeInitial,
    bossAttempts: bossSpawnAttempts(ctx.evo),
  };
}

/**
 * The tier roll inside `get_biter_name` — biter_raffle.lua:60.
 *
 * `math_random(0, total)` tested with `r <= cumulative` means a roll of 0
 * selects the first tier even at weight 0. That is one small biter in 1801 at
 * 100% evolution: an engine quirk, kept because the port is a port.
 */
function rollTier(compiled: Compiled, rng: () => number): number {
  const roll = randomInt(rng, 0, compiled.rollCeiling);
  for (let tier = 0; tier < 4; tier += 1) {
    if (roll <= compiled.cumulative[tier]) return tier;
  }
  return 3;
}

/**
 * A `mixed` group is 1 spitter in 3 (biter_raffle.lua:113). On live that is
 * flavour — the two cost the same. On the test branch a big biter is 25 and a
 * big spitter 13.3, so the split is worth real threat.
 */
function rollUnit(compiled: Compiled, groupType: number, rng: () => number): number {
  // 0 = biter, 1|2 = mixed, 3|4 = spitter, matching `unit_type_raffle`.
  const spitter =
    groupType >= 3 || (groupType >= 1 && groupType <= 2 && randomInt(rng, 1, 3) === 1);
  return rollTier(compiled, rng) * 2 + (spitter ? 1 : 0);
}

export interface WaveComposition {
  groups: number;
  /** Normal units by tier. Fractional, because it is a mean over cycles. */
  units: TierCounts;
  bosses: TierCounts;
  /** The same normal units, split by what they cost — biters against spitters. */
  unitsByKind: KindCounts;
  bossesByKind: KindCounts;
  unitCount: number;
  bossCount: number;
  /** Threat taken off the stock if every normal unit dies. */
  normalRefund: number;
  /** Threat taken off the stock if every boss dies — `value x 26 x H`. */
  bossRefund: number;
  refund: number;
}

/**
 * Running totals, in flat arrays because this is the hot loop. Both are
 * indexed `tier * 2 + (spitter ? 1 : 0)`, the same eight slots as the price
 * table they are counted against.
 */
interface Tally {
  units: Float64Array;
  bosses: Float64Array;
  normalRefund: number;
  bossRefund: number;
}

const newTally = (): Tally => ({
  units: new Float64Array(8),
  bosses: new Float64Array(8),
  normalRefund: 0,
  bossRefund: 0,
});

/**
 * `select_units_around_spawner` — ai.lua:226. One unit group.
 *
 * Budget is `Θ/10`, split into `Θ/20` normals and `Θ/20` bosses once bosses are
 * active. Normals overshoot by up to one unit (the check is `threat < 0`,
 * tested before the unit is priced); bosses subtract first, so they do not.
 */
function spawnGroup(theta: number, compiled: Compiled, rng: () => number, tally: Tally): void {
  const budget = theta / 10;
  let normalBudget = compiled.bossesActive ? budget / 2 : budget;
  const unitCap = Math.min(
    Math.floor(theta * 0.25) + randomInt(rng, 6, 12),
    compiled.groupSize,
  );

  const normalType = randomInt(rng, 0, GROUP_TYPES.length - 1);
  for (let i = 0; i < unitCap; i += 1) {
    const unit = rollUnit(compiled, normalType, rng);
    if (normalBudget < 0) break;
    const value = compiled.price[unit];
    normalBudget -= value;
    tally.units[unit] += 1;
    tally.normalRefund += value;
  }

  if (!compiled.bossesActive) return;

  // A boss costs `value x 20H` from the budget but refunds `value x 26H` when
  // it dies. That 1.3x is why bosses drain more than they were budgeted.
  const bossType = randomInt(rng, 0, GROUP_TYPES.length - 1);
  const bossEquivalent = compiled.health * 20;
  let bossBudget = budget / 2;
  for (let j = 0; j < compiled.bossAttempts; j += 1) {
    const unit = rollUnit(compiled, bossType, rng);
    const value = compiled.price[unit];
    if (bossBudget - value * bossEquivalent < 0) break;
    bossBudget -= value * bossEquivalent;
    tally.bosses[unit] += 1;
    tally.bossRefund += value * BB_CONFIG.healthMultiplierBoss * compiled.health;
  }
}

/**
 * `ceil(get_threat_ratio(force) * 7)` — ai.lua:397, as a share of *combined*
 * threat. An even game is 0.5, which is four groups.
 */
export function groupsForThreatShare(share: number): number {
  if (share <= 0) return 0;
  return Math.min(Math.max(Math.ceil(share * 7), 1), 7);
}

/** An even game: both teams holding the same threat. */
export const EVEN_GAME_GROUPS = groupsForThreatShare(0.5);

/** `storage.bb_threat_income`, summed over one attack cycle. */
export function passiveIncomePerCycle(ruleset: Ruleset, evo: number): number {
  return (ruleset.passivePerMinuteAtEvo(evo) * WAVE_CYCLE_SECONDS) / 60;
}

export interface SteadyState {
  evo: number;
  /** Passive threat earned between one attack on this side and the next. */
  income: number;
  /** The level the stock holds — what a wave is drawn from. */
  theta: number;
  wave: WaveComposition;
  /**
   * False when the wave cannot drain what income adds, so the stock is still
   * climbing. Group and unit caps make this reachable rather than theoretical.
   */
  settled: boolean;
}

const CYCLES = 260;
const BURN_IN = 90;
const SEED = 20240904;

/**
 * The level at which one wave returns exactly what one cycle earns.
 *
 * Seeded from `5 x income` rather than from an empty pool: with four groups
 * each drawing `Θ/20`, a fully cleared wave returns about `Θ/5`, so that is
 * already close and the burn-in only has to correct for bosses. Starting from
 * zero would be a slower way to reach the same answer — the approach is not
 * what a real game does anyway, since a game opens with a send that puts
 * evolution and threat up together and then works them down.
 */
export function steadyState(
  ctx: WaveContext,
  groups: number = EVEN_GAME_GROUPS,
  cycles: number = CYCLES,
  burnIn: number = BURN_IN,
): SteadyState {
  const income = passiveIncomePerCycle(ctx.ruleset, ctx.evo);
  const empty: SteadyState = {
    evo: ctx.evo,
    income,
    theta: 0,
    wave: {
      groups,
      units: zeroTiers(),
      bosses: zeroTiers(),
      unitsByKind: zeroKinds(),
      bossesByKind: zeroKinds(),
      unitCount: 0,
      bossCount: 0,
      normalRefund: 0,
      bossRefund: 0,
      refund: 0,
    },
    settled: true,
  };
  if (income <= 0 || groups <= 0) return empty;

  const compiled = compile(ctx);
  const rng = mulberry32(SEED);
  const totals = newTally();
  let thetaSum = 0;
  let refundSum = 0;
  let samples = 0;

  let theta = 5 * income;
  for (let cycle = 1; cycle <= cycles; cycle += 1) {
    theta += income;
    const spawnTheta = theta;
    const tally = newTally();
    if (spawnTheta > 0) {
      for (let g = 0; g < groups; g += 1) spawnGroup(spawnTheta, compiled, rng, tally);
    }
    const refund = tally.normalRefund + tally.bossRefund;
    theta -= refund;

    if (cycle > burnIn) {
      thetaSum += spawnTheta;
      refundSum += refund;
      for (let slot = 0; slot < 8; slot += 1) {
        totals.units[slot] += tally.units[slot];
        totals.bosses[slot] += tally.bosses[slot];
      }
      totals.normalRefund += tally.normalRefund;
      totals.bossRefund += tally.bossRefund;
      samples += 1;
    }
  }

  const n = Math.max(samples, 1);
  const units = zeroTiers();
  const bosses = zeroTiers();
  const unitsByKind = zeroKinds();
  const bossesByKind = zeroKinds();
  let unitCount = 0;
  let bossCount = 0;
  UNIT_TIERS.forEach((tier, index) => {
    UNIT_KINDS.forEach((kind, kindIndex) => {
      const slot = index * 2 + kindIndex;
      unitsByKind[kind][tier] = totals.units[slot] / n;
      bossesByKind[kind][tier] = totals.bosses[slot] / n;
    });
    units[tier] = unitsByKind.biter[tier] + unitsByKind.spitter[tier];
    bosses[tier] = bossesByKind.biter[tier] + bossesByKind.spitter[tier];
    unitCount += units[tier];
    bossCount += bosses[tier];
  });

  const refundPerCycle = refundSum / n;
  return {
    evo: ctx.evo,
    income,
    theta: thetaSum / n,
    wave: {
      groups,
      units,
      bosses,
      unitsByKind,
      bossesByKind,
      unitCount,
      bossCount,
      normalRefund: totals.normalRefund / n,
      bossRefund: totals.bossRefund / n,
      refund: refundPerCycle,
    },
    // A settled stock returns exactly what it earns. Anything short of that
    // means the caps are holding the wave below the income and Θ is climbing.
    settled: Math.abs(refundPerCycle - income) <= income * 0.05,
  };
}

/* ----------------------------------------------------------------- caching */

/**
 * Solved levels, kept between calls.
 *
 * A solve is ~1–3ms and a sweep is sixty-odd of them, which is fine once and
 * far too slow on every notch of a scroll wheel. The result depends only on
 * `(branch, evolution, threshold, groups)` and the loop is seeded, so the same
 * question always has the same answer and can simply be remembered.
 *
 * The illustrator asks on a dyadic grid (see `gridSamples`), so zooming mostly
 * re-asks for points already in here rather than for sixty new ones.
 *
 * Entries are shared, so **callers must treat a `SteadyState` as immutable** —
 * nothing in the app writes to one, and this is the reason not to start.
 */
const solved = new Map<string, SteadyState>();

/**
 * Dropped wholesale rather than evicted one at a time. The set only grows
 * without bound while a scenario slider is being dragged — every parameter set
 * is a new `cacheKey`, and none of the old ones will be asked for again — so
 * the cheap thing and the right thing agree.
 */
const SOLVED_LIMIT = 5000;

export function steadyStateCached(
  ctx: WaveContext,
  groups: number = EVEN_GAME_GROUPS,
): SteadyState {
  const key = `${ctx.ruleset.cacheKey}|${ctx.maxReanimThresh}|${groups}|${ctx.evo}`;
  const hit = solved.get(key);
  if (hit) return hit;
  const value = steadyState(ctx, groups);
  if (solved.size >= SOLVED_LIMIT) solved.clear();
  solved.set(key, value);
  return value;
}

/** The same solve across an evolution range — the illustrator's two charts. */
export function steadyStateByEvo(
  ruleset: Ruleset,
  evos: number[],
  maxReanimThresh: number = DEFAULTS.maxReanimThresh,
  groups: number = EVEN_GAME_GROUPS,
): SteadyState[] {
  return evos.map((evo) => steadyStateCached({ ruleset, evo, maxReanimThresh }, groups));
}

/** Evenly spaced evolution samples, inclusive of both ends. */
export function evoSamples(from: number, to: number, count: number): number[] {
  return Array.from({ length: count }, (_, i) => from + ((to - from) * i) / (count - 1));
}

/**
 * Sample points for a zoomable window, on a **dyadic** grid.
 *
 * The step is always `GRID_BASE / 2^k`, which makes every coarser grid an exact
 * subset of every finer one. Zooming therefore either keeps the step — and every
 * point is one already solved — or halves/doubles it, where half the points
 * still are. A prettier 1 / 2 / 2.5 / 5 ladder would look the same on screen and
 * miss the cache almost every time, because a 0.02 grid and a 0.025 grid share
 * almost nothing.
 *
 * The window's own edge is appended when it does not land on the grid, so the
 * plot still reaches the right-hand side. That one point is the only guaranteed
 * miss in a sweep.
 */
const GRID_BASE = 0.4;
const GRID_MIN_STEP = GRID_BASE / 2 ** 12;

export function gridSamples(maxEvo: number, target = 66): number[] {
  if (!(maxEvo > 0)) return [0];
  const wanted = maxEvo / Math.max(target, 1);
  const k = Math.round(Math.log2(GRID_BASE / wanted));
  const step = Math.max(GRID_BASE / 2 ** Math.max(k, 0), GRID_MIN_STEP);

  const out: number[] = [];
  const last = Math.floor(maxEvo / step + 1e-9);
  for (let i = 0; i <= last; i += 1) out.push(i * step);
  if (out[out.length - 1] < maxEvo - step * 1e-9) out.push(maxEvo);
  return out;
}

/** What one spawner returns at this evolution, in the branch's threat units. */
export function spawnerValue(ruleset: Ruleset, evo: number): number {
  return (ruleset.threatValues['biter-spawner'] ?? 0) * spawnerThreatFactor(evo);
}
